import { KeyboardEvent } from 'react';

type EventCallback = <T>(e?: KeyboardEvent<T>) => void;

/**
 * Invokes a callback (with the triggering event) if any of the specified keys are pressed.
 */
export const handleKeys = <T>(
  event: KeyboardEvent<T>,
  keys: string | string[],
  callback: EventCallback
): void => {
  const keyArray = Array.isArray(keys) ? keys : [keys];

  if (keyArray.includes(event.key)) {
    callback(event);
  }
};

/**
 * Returns an event handler that will invoke a callback if triggered by any of the specified keys.
 */
export const createKeyHandler =
  (keys: string | string[], callback: EventCallback) =>
  <T>(event: KeyboardEvent<T>): void => {
    handleKeys(event, keys, callback);
  };

const BROWSER_CLICK_KEYS = ['Enter', ' '];

/**
 * Returns an event handler that will invoke a callback if triggered by the Enter or Space keys.
 * Use on non <button> elements to reproduce browser behavior (i.e. triggering onClick for Enter & Space keys).
 *
 * Note: Also prevents default to avoid having the space key scroll the page.
 */
export const createKeyClickHandler =
  (callback: EventCallback) =>
  <T>(event: KeyboardEvent<T>): void => {
    handleKeys(event, BROWSER_CLICK_KEYS, (e) => {
      e?.preventDefault();
      callback(e);
    });
  };
